import { useQuery } from '@tanstack/react-query';
import { orgSetupApi } from '@/lib/org-setup-api';
import { queryKeys, orgScope } from '@/lib/query-keys';
import type { SandboxProviderHealthResponse } from '@reflex/shared';

/**
 * Reads whether the org's stored Runloop (sandbox provider) key actually
 * works, not just whether it is set. The setup gate polls this for a
 * bootstrapped org so a later key revocation re-routes the user back into
 * the wizard's API-keys step instead of silently failing at point-of-use.
 *
 * The server caches the underlying `/v1/accounts/me` verification (~60s),
 * and we mirror that with `staleTime` so navigation doesn't re-fetch on
 * every render. A window-focus refetch picks up a fix made in another tab.
 *
 * Pass `enabled: false` (e.g. no active org, or the org isn't bootstrapped
 * yet) to skip the request entirely.
 */
export function useSandboxHealth(orgId: string | null | undefined, enabled = true) {
  return useQuery<SandboxProviderHealthResponse>({
    queryKey: queryKeys.sandboxHealth(orgScope(orgId)),
    queryFn: () => orgSetupApi.getSandboxHealth(orgId!),
    enabled: enabled && !!orgId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
